const DAILY_TARGET_HOURS = 8;

const toDateKey = (value) => {
  if (!value) {
    return "";
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (monthId) => {
  const cleanMonthId = /^\d{4}-\d{2}$/.test(String(monthId || "")) ? monthId : toDateKey(new Date()).slice(0, 7);
  const [year, month] = cleanMonthId.split("-").map(Number);
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);
  return {
    monthId: cleanMonthId,
    startKey: toDateKey(startDate),
    endKey: toDateKey(endDate),
  };
};

const addDays = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

const formatHours = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }
  return Number(numericValue.toFixed(2));
};

const getItemDateKey = (item = {}, fallbackDateKey = "") =>
  toDateKey(item.workDate || item.date || item.workDateKey || item.Date__c || fallbackDateKey);

const getItemStatus = (item = {}, fallbackStatus = "") =>
  String(item.status || item.Status__c || fallbackStatus || "Draft").trim().toLowerCase();

const getItemHours = (item = {}) => {
  const rawValue = item.hours ?? item.Hours__c ?? item.workingHours ?? item.totalHours ?? 0;
  const numericValue = Number(rawValue);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const isSubmittedCoverageStatus = (status = "") =>
  ["approved", "submitted", "review", "in review"].includes(String(status || "").trim().toLowerCase());

const isLeaveItem = (item = {}) => {
  if (item.isLeaveEntry || item.isLeaveTimeEntry || item.leaveEntry || item.leaveTimeEntry || item.isLeave || item.leaveGenerated) {
    return true;
  }
  const descriptor = [
    item.description,
    item.notes,
    item.source,
    item.entryType,
    item.type,
    item.recordType,
    item.category,
  ].join(" ").toLowerCase();
  return descriptor.includes("auto-created from approved leave") || descriptor.includes("took leave") || descriptor.includes("leave time entry");
};

const getDayLabel = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateKey;
  }
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    weekday: "short",
  });
};

const getTimesheetItemsByDate = (monthTimesheet = []) =>
  monthTimesheet.reduce((map, week) => {
    Object.values(week?.entries || {}).forEach((entry = {}) => {
      if (entry.isOutsideMonth) {
        return;
      }
      const fallbackDateKey = toDateKey(entry.workDate || entry.date);
      (entry.items || []).forEach((item) => {
        const dateKey = getItemDateKey(item, fallbackDateKey);
        if (!dateKey) {
          return;
        }
        if (!map.has(dateKey)) {
          map.set(dateKey, []);
        }
        map.get(dateKey).push({
          ...item,
          status: item.status || entry.status || "Draft",
        });
      });
    });
    return map;
  }, new Map());

export const calculateTimesheetApprovalProgress = ({
  holidays = [],
  monthId,
  monthTimesheet = [],
  today = new Date(),
} = {}) => {
  const { monthId: targetMonthId, startKey, endKey } = getMonthBounds(monthId);
  const todayKey = toDateKey(today);
  const previousDayKey = todayKey ? addDays(todayKey, -1) : "";
  const riskEndKey =
    previousDayKey && previousDayKey.slice(0, 7) === targetMonthId
      ? previousDayKey
      : todayKey > endKey
        ? endKey
        : "";
  const targetEndKey =
    todayKey && todayKey.slice(0, 7) === targetMonthId
      ? todayKey
      : todayKey > endKey
        ? endKey
        : "";
  const holidayKeys = new Set(
    holidays.map((holiday) => toDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.date || holiday?.holidayDate)).filter(Boolean)
  );
  const itemsByDate = getTimesheetItemsByDate(monthTimesheet);
  const submissionDays = [];
  let workingDays = 0;
  let targetWorkingDays = 0;
  let elapsedWorkingDays = 0;
  let submittedHours = 0;

  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = addDays(dateKey, 1)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isHoliday = holidayKeys.has(dateKey);
    if (isWeekend || isHoliday) {
      continue;
    }

    workingDays += 1;
    const items = itemsByDate.get(dateKey) || [];
    const submittedDayHours = items.reduce((sum, item) => {
      const status = getItemStatus(item);
      if (isSubmittedCoverageStatus(status) || isLeaveItem(item)) {
        return sum + getItemHours(item);
      }
      return sum;
    }, 0);

    if (targetEndKey && dateKey <= targetEndKey) {
      targetWorkingDays += 1;
      submittedHours += submittedDayHours;
      const missingHours = Math.max(DAILY_TARGET_HOURS - submittedDayHours, 0);
      submissionDays.push({
        dateKey,
        label: getDayLabel(dateKey),
        expectedHours: DAILY_TARGET_HOURS,
        submittedHours: formatHours(submittedDayHours),
        missingHours: formatHours(missingHours),
        status:
          submittedDayHours >= DAILY_TARGET_HOURS
            ? "Submitted"
            : submittedDayHours > 0
              ? "Partially submitted"
              : "Not submitted",
      });
    }

    const isElapsed = riskEndKey && dateKey <= riskEndKey;
    if (!isElapsed) {
      continue;
    }

    elapsedWorkingDays += 1;
  }

  const monthlyTargetHours = workingDays * DAILY_TARGET_HOURS;
  const targetHours = targetWorkingDays * DAILY_TARGET_HOURS;
  const elapsedTargetHours = elapsedWorkingDays * DAILY_TARGET_HOURS;
  const safeSubmittedHours = formatHours(submittedHours);
  const shortfallHours = formatHours(Math.max(targetHours - safeSubmittedHours, 0));
  const lopDays = formatHours(shortfallHours / DAILY_TARGET_HOURS);

  return {
    submittedHours: safeSubmittedHours,
    elapsedTargetHours,
    elapsedWorkingDays,
    monthId: targetMonthId,
    progressPercent: targetHours ? Math.min(100, Math.max(0, (safeSubmittedHours / targetHours) * 100)) : 0,
    remainingHours: shortfallHours,
    lopDays,
    hasLopRisk: lopDays > 0,
    monthlyTargetHours,
    submissionDays,
    targetHours,
    targetWorkingDays,
    workingDays,
  };
};
